/**
 * Typed accessors for the `/storage/*` Backend_API routes (Inventory Storage
 * Locations feature). Mirrors the existing `useDevicesApi()` /
 * `useCatalogApi()` pattern: one hook returning small `request` wrappers,
 * memoized on the underlying API client identity so consumers can use them as
 * `useEffect` / `useCallback` deps.
 *
 * Every wire shape here mirrors the response JSON produced by
 * `packages/api/src/handlers/storage.ts` — the API server is the single source
 * of truth for these payloads. Domain shapes are imported from `@tcvs/shared`
 * (via `../shared/types`) and never redeclared; only the per-route response
 * envelopes (which are wire shapes, not domain types) are declared locally.
 *
 * All routes sit behind the existing Cognito authorizer; the SPA reuses the
 * shared auth plumbing (`useApi` → bearer token) and never involves a device
 * credential (Requirement 14.5).
 */

import { useMemo } from 'react';
import { useApi } from '../api';
import type { ApiResult } from '../auth/api-client';
import type {
  Box,
  ContainerListing,
  ListableContainerType,
  LocationResolution,
  PicklistEntry,
  QrToken,
  Rack,
  RunBatchResolution,
  Shelf,
  StorageRow,
  StorageBatch,
  StorageBatchWorkflowStatus,
  StorageEntity,
  StorageTransaction,
} from '../shared/types';

// ---------------------------------------------------------------------------
// The `{type}` path segment used by collection routes.
// ---------------------------------------------------------------------------

/** Plural collection segment accepted by `/storage/{type}/...` routes. */
export type ContainerType = 'racks' | 'shelves' | 'boxes' | 'rows' | 'batches';
/** `{type}` for the label/transaction routes, which also accept a card. */
export type LabelType = ContainerType | 'cards';

// ---------------------------------------------------------------------------
// Create-entity responses (HTTP 201 `{ entity }`)
// ---------------------------------------------------------------------------

export interface CreateRackResponse {
  entity: Rack;
}
export interface CreateShelfResponse {
  entity: Shelf;
}
export interface CreateBoxResponse {
  entity: Box;
}
export interface CreateRowResponse {
  entity: StorageRow;
}
export interface CreateBatchResponse {
  entity: StorageBatch;
}

// ---------------------------------------------------------------------------
// Putaway / move / pick mutation subjects + responses
// ---------------------------------------------------------------------------

/** The subject of a putaway, mirroring the handler's `toPutawaySubject`. */
export type PutawaySubject =
  | { type: 'SHELF'; entityId: string }
  | { type: 'BOX'; entityId: string }
  | { type: 'ROW'; entityId: string }
  | { type: 'BATCH'; entityId: string }
  | { type: 'CARD'; cardId: string };

/** The subject of a move, mirroring the handler's `toMoveSubject`. */
export type MoveSubject =
  | { type: 'SHELF'; entityId: string }
  | { type: 'BOX'; entityId: string }
  | { type: 'ROW'; entityId: string }
  | { type: 'BATCH'; entityId: string };

/** Response for the legacy typed putaway endpoint shape. */
export interface TypedPutawayResponse {
  status: 'ok' | 'no-op';
  operation: 'PUTAWAY' | 'MOVE';
  subject: PutawaySubject;
  targetId: string;
  message?: string;
}

/** Response for the approved type-free container putaway shape. */
export interface PutawayResponse {
  status: 'ok' | 'no-op';
  operation: 'PUTAWAY' | 'MOVE';
  subject: PutawayContainerRef;
  target: PutawayContainerRef;
  expectedTargetType: PutawayContainerType;
  resolution: { subject: PutawayReferenceSource; target: PutawayReferenceSource };
  message?: string;
}

export type PutawayContainerType = 'RACK' | 'SHELF' | 'BOX' | 'ROW' | 'BATCH';
export type PutawayReferenceSource = 'canonical' | 'short-id';

/** Server-resolved container identity returned by type-free Putaway. */
export interface PutawayContainerRef {
  entityId: string;
  entityType: PutawayContainerType;
}

/** Read-only response for the collision-aware Putaway scanner preflight. */
export interface PutawayReferenceResolution {
  kind: 'putaway-container';
  entity: PutawayContainerRef;
  resolution: PutawayReferenceSource;
}

type PutawayErrorBody = {
  field?: 'subjectRef' | 'targetRef';
  expectedTargetType?: string;
  candidates?: Array<{ entityId: string; entityType: string }>;
};

/** Turn the type-free resolver envelope into operator-facing inline guidance. */
export function putawayFailureMessage(result: {
  ok: false;
  error: string;
  message?: string;
  body?: unknown;
}): string {
  const body = (result.body ?? {}) as PutawayErrorBody;
  const field = body.field === 'targetRef' ? 'destination' : 'item';
  switch (result.error) {
    case 'malformed-container-reference':
      return `The ${field} must be a container QR label or short ID.`;
    case 'container-not-found':
      return `No container matches that ${field}.`;
    case 'ambiguous-container-reference': {
      const candidates = body.candidates
        ?.map((candidate) => `${candidate.entityType} ${candidate.entityId}`)
        .join(', ');
      return candidates === undefined || candidates === ''
        ? `That ${field} short ID is ambiguous. Scan the full canonical label.`
        : `That ${field} short ID is ambiguous. Scan the full canonical label: ${candidates}.`;
    }
    case 'invalid-hierarchy':
      return body.expectedTargetType === undefined
        ? 'The item cannot be put away into that destination.'
        : `This item requires a ${body.expectedTargetType} destination.`;
    default:
      return result.message ?? result.error;
  }
}

export interface MoveResponse {
  status: 'ok' | 'no-op';
  operation: 'MOVE';
  subject: MoveSubject;
  destParentId: string;
  message?: string;
}

export interface PickResponse {
  status: 'ok';
  operation: 'PICK';
  cardId: string;
  sourceBatchId?: string;
  shortCode?: string;
  sku?: string;
}

export interface DeleteStorageEntityResponse {
  status: 'ok';
  operation: 'DELETE_STORAGE_BATCH' | 'DELETE_STORAGE_ENTITY';
  entityId: string;
  entityType?: 'RACK' | 'SHELF' | 'BOX' | 'ROW' | 'BATCH';
  cardCount?: number;
}

// ---------------------------------------------------------------------------
// Location, picklist, contents, labels, scan, transactions responses
// ---------------------------------------------------------------------------

export interface CardLocationResponse {
  cardId: string;
  location: LocationResolution;
  /**
   * Set only when the supplied identifier was a short code that resolved to this
   * card; echoes the canonical short code the backend matched (short-card-code
   * resolution on the location path).
   */
  shortCode?: string;
}

export interface PicklistResponse {
  entries: PicklistEntry[];
}

/**
 * Contents listing. `kind` discriminates the `items` payload: container kinds
 * are `StorageEntity[]`; `cards` are STORED `CardPlacement`-shaped
 * items. `nextPageToken` is present only when more pages remain.
 */
export interface ContentsResponse {
  entityId: string;
  kind: 'shelves' | 'boxes' | 'rows' | 'batches' | 'cards';
  items: unknown[];
  nextPageToken?: string;
}

/** Entity label response (Shelf / Box / Storage_Batch). */
export interface EntityLabelResponse {
  entityId: string;
  entityType: StorageEntity['entityType'];
  token: string;
  parsed: QrToken;
}

/** Card label response (`tcvs:card:<cardId>`, Req 9.2). */
export interface CardLabelResponse {
  cardId: string;
  token: string;
  parsed: QrToken;
  /** The card's human-friendly short code, when it has one (label caption). */
  shortCode?: string;
}

export type LabelResponse = EntityLabelResponse | CardLabelResponse;

/** One card in a multi-match SKU scan result. */
export interface ScanCardMatch {
  cardId: string;
  location: LocationResolution;
  shortCode?: string;
}

/**
 * Scan resolution. `/storage/scan` accepts a QR token, a short code, a literal
 * cardId, or an external-service SKU. A token/short-code/cardId resolves to a
 * single `card` or `entity`. A SKU resolves to a single `card` when exactly one
 * card carries it, or a `cards` LIST when more than one does (SKU is not
 * enforced unique, so we surface all matches rather than guess). When resolved
 * via a short code the card carries the canonical `shortCode`.
 */
export type ScanResponse =
  | { kind: 'card'; cardId: string; location: LocationResolution; shortCode?: string }
  | { kind: 'entity'; entity: StorageEntity; location: EntityLocationResolution }
  | { kind: 'cards'; sku: string; cards: ScanCardMatch[] };

/**
 * Mirror of the API's `EntityLocationResolution` (scan resolution for a
 * Shelf / Box / Storage_Batch token). Declared locally because it is an API
 * wire shape rather than a `@tcvs/shared` domain type.
 */
export type EntityLocationResolution =
  | {
      status: 'PLACED';
      entityId: string;
      entityType: StorageEntity['entityType'];
      path: {
        rack?: { entityId: string; rackCode: string };
        shelf?: { entityId: string; shelfCode: string };
        box?: { entityId: string };
        row?: { entityId: string; rowCode?: string };
        batch?: { entityId: string };
      };
    }
  | {
      status: 'UNPLACED';
      entityId: string;
      entityType: StorageEntity['entityType'];
      path?: {
        rack?: { entityId: string; rackCode: string };
        shelf?: { entityId: string; shelfCode: string };
        box?: { entityId: string };
        row?: { entityId: string; rowCode?: string };
        batch?: { entityId: string };
      };
    };

/**
 * Mirror of the API's occupancy result (`GET /storage/{type}/{id}/occupancy`
 * and the `occupancy` field of the entity-detail response). `tracked:false` when
 * the entity does not have occupancy tracking enabled; otherwise the computed
 * child count + optional capacity / remaining.
 */
export type OccupancyResult =
  | { entityId: string; entityType: StorageEntity['entityType']; tracked: false }
  | {
      entityId: string;
      entityType: StorageEntity['entityType'];
      tracked: true;
      childCount: number;
      capacity?: number;
      remainingCapacity?: number;
    };

/**
 * `GET /storage/{type}/{id}` — a single Shelf / Box / Storage_Batch's own record
 * plus its resolved location and occupancy, backing the entity detail page
 * (parallels `GET /cards/{id}` returning a card + location).
 */
export interface StorageEntityDetailResponse {
  entity: StorageEntity;
  location: EntityLocationResolution;
  occupancy: OccupancyResult;
  deletion: { allowed: boolean; reason?: 'not-empty' };
  batchStatus?: StorageBatchWorkflowStatus;
}

/**
 * `POST /storage/scan-putaway` result. On success the handler returns
 * `{ status:'ok', batchToRow:'committed' }`.
 */
export type ScanPutawaySuccessResponse = {
  status: 'ok';
  batchToRow: 'committed';
};
export type ScanPutawayResponse = ScanPutawaySuccessResponse;

export interface TransactionsResponse {
  entityRef: string;
  items: StorageTransaction[];
  nextPageToken?: string;
}

// ---------------------------------------------------------------------------
// Request input shapes
// ---------------------------------------------------------------------------

export interface CreateRackInput {
  rackCode: string;
  capacity?: number;
  occupancyEnabled?: boolean;
}
export interface CreateShelfInput {
  shelfCode: string;
  capacity?: number;
  occupancyEnabled?: boolean;
}
export interface CreateBoxInput {
  capacity?: number;
  occupancyEnabled?: boolean;
}
export interface CreateRowInput {
  rowCode?: string;
  capacity?: number;
  occupancyEnabled?: boolean;
}
export interface CreateBatchInput {
  sourceRunId?: string;
  capacity?: number;
  occupancyEnabled?: boolean;
}

export interface ScanPutawayInput {
  batchToken: string;
  rowToken: string;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/** React hook returning typed accessors for the `/storage/*` routes. */
export function useStorageApi() {
  const api = useApi();
  return useMemo(
    () => ({
      // --- create ---
      createRack: (input: CreateRackInput): Promise<ApiResult<CreateRackResponse>> =>
        api.request<CreateRackResponse>('/storage/racks', { method: 'POST', body: input }),
      createShelf: (input: CreateShelfInput): Promise<ApiResult<CreateShelfResponse>> =>
        api.request<CreateShelfResponse>('/storage/shelves', { method: 'POST', body: input }),
      createBox: (input: CreateBoxInput = {}): Promise<ApiResult<CreateBoxResponse>> =>
        api.request<CreateBoxResponse>('/storage/boxes', { method: 'POST', body: input }),
      createRow: (input: CreateRowInput = {}): Promise<ApiResult<CreateRowResponse>> =>
        api.request<CreateRowResponse>('/storage/rows', { method: 'POST', body: input }),
      createBatch: (input: CreateBatchInput = {}): Promise<ApiResult<CreateBatchResponse>> =>
        api.request<CreateBatchResponse>('/storage/batches', { method: 'POST', body: input }),

      // --- mutate ---
      putaway: (
        subject: PutawaySubject,
        targetId: string,
      ): Promise<ApiResult<TypedPutawayResponse>> =>
        api.request<TypedPutawayResponse>('/storage/putaway', {
          method: 'POST',
          body: { subject, targetId },
        }),
      putawayByReference: (
        subjectRef: string,
        targetRef: string,
      ): Promise<ApiResult<PutawayResponse>> =>
        api.request<PutawayResponse>('/storage/putaway', {
          method: 'POST',
          body: { subjectRef, targetRef },
        }),
      resolvePutawayReference: (value: string): Promise<ApiResult<PutawayReferenceResolution>> =>
        api.request<PutawayReferenceResolution>('/storage/scan', {
          method: 'POST',
          body: { value, mode: 'putaway-container' },
        }),
      move: (subject: MoveSubject, destParentId: string): Promise<ApiResult<MoveResponse>> =>
        api.request<MoveResponse>('/storage/move', {
          method: 'POST',
          body: { subject, destParentId },
        }),
      pick: (cardId: string): Promise<ApiResult<PickResponse>> =>
        api.request<PickResponse>('/storage/pick', { method: 'POST', body: { cardId } }),
      deleteContainer: (
        type: ContainerType,
        id: string,
      ): Promise<ApiResult<DeleteStorageEntityResponse>> =>
        api.request<DeleteStorageEntityResponse>(`/storage/${type}/${encodeURIComponent(id)}`, {
          method: 'DELETE',
        }),

      // --- resolve / read ---
      cardLocation: (cardId: string): Promise<ApiResult<CardLocationResponse>> =>
        api.request<CardLocationResponse>(`/storage/cards/${encodeURIComponent(cardId)}/location`, {
          method: 'GET',
        }),
      picklist: (cardIds: string[]): Promise<ApiResult<PicklistResponse>> =>
        api.request<PicklistResponse>('/storage/picklist', {
          method: 'POST',
          body: { cardIds },
        }),
      contents: (
        type: ContainerType,
        id: string,
        page?: string,
      ): Promise<ApiResult<ContentsResponse>> =>
        api.request<ContentsResponse>(`/storage/${type}/${encodeURIComponent(id)}/contents`, {
          method: 'GET',
          ...(page !== undefined ? { query: { page } } : {}),
        }),
      // Single-entity detail (own record + location + occupancy) for the
      // Shelf/Box/Storage_Batch detail page.
      entityDetail: (
        type: ContainerType,
        id: string,
      ): Promise<ApiResult<StorageEntityDetailResponse>> =>
        api.request<StorageEntityDetailResponse>(`/storage/${type}/${encodeURIComponent(id)}`, {
          method: 'GET',
        }),
      labels: (type: LabelType, id: string): Promise<ApiResult<LabelResponse>> =>
        api.request<LabelResponse>(`/storage/${type}/${encodeURIComponent(id)}/labels`, {
          method: 'GET',
        }),
      transactions: (
        type: LabelType,
        id: string,
        page?: string,
      ): Promise<ApiResult<TransactionsResponse>> =>
        api.request<TransactionsResponse>(
          `/storage/${type}/${encodeURIComponent(id)}/transactions`,
          { method: 'GET', ...(page !== undefined ? { query: { page } } : {}) },
        ),

      // --- list containers ---
      listContainers: (
        type: ListableContainerType,
        page?: string,
        options?: {
          includeStatus?: boolean;
          includePreviews?: boolean;
          incompleteOnly?: boolean;
          limit?: number;
        },
      ): Promise<ApiResult<ContainerListing>> =>
        api.request<ContainerListing>(`/storage/${type}`, {
          method: 'GET',
          ...(page !== undefined ||
          options?.includeStatus === true ||
          options?.includePreviews === true ||
          options?.incompleteOnly === true ||
          options?.limit !== undefined
            ? {
                query: {
                  ...(page !== undefined ? { page } : {}),
                  ...(options?.includeStatus === true ? { includeStatus: 'true' } : {}),
                  ...(options?.includePreviews === true ? { includePreviews: 'true' } : {}),
                  ...(options?.incompleteOnly === true ? { incompleteOnly: 'true' } : {}),
                  ...(options?.limit !== undefined ? { limit: String(options.limit) } : {}),
                },
              }
            : {}),
        }),

      // --- resolve run ---
      resolveRunBatch: (runId: string): Promise<ApiResult<RunBatchResolution>> =>
        api.request<RunBatchResolution>(`/storage/batches/by-run/${encodeURIComponent(runId)}`, {
          method: 'GET',
        }),

      // --- scan ---
      scan: (value: string): Promise<ApiResult<ScanResponse>> =>
        api.request<ScanResponse>('/storage/scan', { method: 'POST', body: { value } }),
      scanPutaway: (input: ScanPutawayInput): Promise<ApiResult<ScanPutawayResponse>> =>
        api.request<ScanPutawayResponse>('/storage/scan-putaway', {
          method: 'POST',
          body: input,
        }),
    }),
    [api],
  );
}
